import React from "react";
import { useNavigate } from "react-router-dom";
import { Agent } from "../../model_gen/agent_base";
import { Application } from "../../model_gen/application";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { Combobox } from "../ui/combobox";
import { Checkbox } from "../ui/checkbox";
import { Tooltip } from "../ui/tooltip";
import { 
  TrashIcon,
  ArrowUpDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  UserIcon,
  MoreHorizontalIcon,
  CopyIcon
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Label } from "../ui/label";
import { cn } from "../../lib/utils";
import moment from "moment";

interface AgentWithApplication extends Agent {
  application_name?: string;
}

interface SortConfig {
  key: keyof AgentWithApplication | null;
  direction: "asc" | "desc";
}

interface FilterConfig {
  applicationId: string | null;
  includeSubagents: boolean;
}

interface AgentsTableProps {
  agents: AgentWithApplication[];
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  sortConfig: SortConfig;
  setSortConfig: (config: SortConfig) => void;
  onDelete: (agentId: number) => void;
  onClone: (agentId: number, newAgentName: string, newApplicationName: string) => void;
  onCreateAgent: () => void;
  deleteDialogOpen: number | null;
  setDeleteDialogOpen: (agentId: number | null) => void;
  pageSize: number;
  totalAgents: number;
  // Filter props
  applications: Application[];
  filterConfig: FilterConfig;
  setFilterConfig: (config: FilterConfig) => void;
  onClearFilters: () => void;
}

const AgentsTable: React.FC<AgentsTableProps> = ({
  agents,
  isLoading,
  currentPage,
  setCurrentPage,
  sortConfig,
  setSortConfig,
  onDelete,
  onClone,
  onCreateAgent,
  deleteDialogOpen,
  setDeleteDialogOpen,
  pageSize,
  totalAgents,
  applications,
  filterConfig,
  setFilterConfig,
  onClearFilters
}) => {
  const navigate = useNavigate();
  const [deletingAgents, setDeletingAgents] = React.useState<Set<number>>(new Set());
  const [cloneDialogOpen, setCloneDialogOpen] = React.useState<number | null>(null);
  const [cloneAgentName, setCloneAgentName] = React.useState<string>("");
  const [cloneApplicationName, setCloneApplicationName] = React.useState<string>("");

  const handleDelete = (agentId: number) => {
    setDeletingAgents(prev => new Set(prev).add(agentId));
    onDelete(agentId);
    // Close dialog after a brief delay to ensure deletion process starts
    setTimeout(() => setDeleteDialogOpen(null), 100);
  };

  const handleCloneClick = (agent: AgentWithApplication) => {
    if (agent.id) {
      setCloneAgentName(`${agent.agent_name || "Agent"} (Copy)`);
      setCloneApplicationName(`${agent.application_name || "Application"} (Copy)`);
      setCloneDialogOpen(agent.id);
    }
  };

  const handleClone = () => {
    if (cloneDialogOpen && cloneAgentName.trim() && cloneApplicationName.trim()) {
      onClone(cloneDialogOpen, cloneAgentName.trim(), cloneApplicationName.trim());
      setCloneDialogOpen(null);
      setCloneAgentName("");
      setCloneApplicationName("");
    }
  };

  const handleRowClick = (agentId: number) => {
    // Don't navigate if agent is being deleted or delete dialog is open for this agent
    if (!deletingAgents.has(agentId) && deleteDialogOpen !== agentId && cloneDialogOpen !== agentId) {
      navigate(`/agent/${agentId}`);
    }
  };

  const handleSort = (key: keyof AgentWithApplication) => {
    let direction: "asc" | "desc" = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  // Use agents directly since sorting is handled by parent component

  const totalPages = Math.ceil(totalAgents / pageSize);

  const formatDate = (timestamp: number | null | undefined) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const formatTime = (timestamp: number | null | undefined) => {
    if (!timestamp || timestamp <= 0) return "";
    const date = moment(timestamp);
    return date.format("h:mm A");
  };

  const SortableHeader: React.FC<{ 
    children: React.ReactNode; 
    sortKey: keyof AgentWithApplication;
    className?: string;
  }> = ({ children, sortKey, className }) => (
    <TableHead className={cn("cursor-pointer hover:bg-accent/20 transition-colors", className)}>
      <button
        onClick={() => handleSort(sortKey)}
        className={cn(
          "flex items-center gap-2 font-gc-med text-muted-foreground hover:text-foreground transition-colors",
          sortConfig.key === sortKey && "text-maitai-green"
        )}
      >
        {children}
        <ArrowUpDownIcon className={cn(
          "w-3 h-3 transition-colors",
          sortConfig.key === sortKey && "text-maitai-green"
        )} />
      </button>
    </TableHead>
  );

  // Check if any filters are active (non-default state)
  // Default: no application selected (null) and exclude subagents (false)
  const hasActiveFilters = filterConfig.applicationId !== null || filterConfig.includeSubagents !== false;

  const renderLoadingState = () => (
    <Table>
      <TableHeader>
        <TableRow className="border-b border-border hover:bg-transparent">
          <TableHead className="w-[30%]">Name</TableHead>
          <TableHead className="w-[25%]">Application</TableHead>
          <TableHead className="w-[25%]">Description</TableHead>
          <TableHead className="w-[15%]">Created</TableHead>
          <TableHead className="w-[5%]"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...Array(8)].map((_, i) => (
          <TableRow key={i} className="border-b border-border/50">
            <TableCell className="py-4">
              <div className="h-4 bg-muted rounded animate-pulse" />
            </TableCell>
            <TableCell className="py-4">
              <div className="h-4 bg-muted rounded w-3/4 animate-pulse" />
            </TableCell>
            <TableCell className="py-4">
              <div className="space-y-2">
                <div className="h-3 bg-muted rounded animate-pulse" />
                <div className="h-3 bg-muted rounded w-2/3 animate-pulse" />
              </div>
            </TableCell>
            <TableCell className="py-4">
              <div className="h-4 bg-muted rounded w-20 animate-pulse" />
            </TableCell>
            <TableCell className="py-4">
              <div className="h-8 w-8 bg-muted rounded animate-pulse" />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <>
      {/* Filter Controls - Always show filters */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-end">
          <div className="flex items-center gap-4">
            {/* Application Filter */}
            <div className="flex items-center gap-2">
              <Combobox
                options={[
                  { value: "all", label: "All Applications" },
                  ...applications
                    .filter((app) => app.id != null) // Only include applications with valid IDs
                    .map((app) => ({
                      value: String(app.id),
                      label: app.application_name || "Unnamed Application"
                    }))
                ]}
                value={filterConfig.applicationId || "all"}
                onValueChange={(value) =>
                  setFilterConfig({
                    ...filterConfig,
                    applicationId: !value || value === "all" ? null : value,
                  })
                }
                placeholder="Select application..."
                searchPlaceholder="Search applications..."
                emptyMessage="No applications found."
                className="w-64 max-h-64"
              />
            </div>

            {/* Include Subagents Checkbox */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="include-subagents"
                checked={filterConfig.includeSubagents}
                onCheckedChange={(checked) =>
                  setFilterConfig({
                    ...filterConfig,
                    includeSubagents: checked === true,
                  })
                }
              />
              <label
                htmlFor="include-subagents"
                className="text-sm text-muted-foreground cursor-pointer"
              >
                Include subagents
              </label>
            </div>

            {/* Clear Filters Button - Always visible */}
            <Button
              variant="outline"
              onClick={onClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </Button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      {isLoading ? (
        renderLoadingState()
      ) : agents.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="space-y-4">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center">
              <UserIcon className="w-8 h-8 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-gc-med text-foreground">
                No agents found
              </h3>
              <p className="text-muted-foreground max-w-md">
                {hasActiveFilters 
                  ? "No agents match the current filters. Try adjusting your filters or create a new agent."
                  : "Create your first agent to start orchestrating intents and building intelligent workflows."
                }
              </p>
            </div>
            <Button onClick={onCreateAgent} className="gap-2">
              <PlusIcon className="w-4 h-4" />
              Create Your First Agent
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Table>
        <TableHeader>
          <TableRow className="border-b border-border hover:bg-transparent">
            <SortableHeader sortKey="agent_name" className="w-[30%]">
              Name
            </SortableHeader>
            <SortableHeader sortKey="application_name" className="w-[25%]">
              Application
            </SortableHeader>
            <SortableHeader sortKey="description" className="w-[25%]">
              Description
            </SortableHeader>
            <SortableHeader sortKey="date_created" className="w-[15%]">
              Created
            </SortableHeader>
            <TableHead className="w-[5%]"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {agents.map((agent) => (
            <TableRow
              key={agent.id || 0}
              className="cursor-pointer hover:bg-accent/20 transition-colors border-b border-border/50"
              onClick={() => agent.id && handleRowClick(agent.id)}
            >
              <TableCell className="py-4">
                <div className="font-gc-med text-foreground hover:text-maitai-green transition-colors">
                  {agent.agent_name || "Unnamed Agent"}
                </div>
              </TableCell>
              <TableCell className="py-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/application/${agent.application_id}`);
                  }}
                  className="text-sm text-maitai-green hover:text-maitai-green/80 hover:underline transition-colors font-gc-med"
                >
                  {agent.application_name || "Unknown Application"}
                </button>
              </TableCell>
              <TableCell className="py-4">
                <Tooltip content={agent.description || "No description"}>
                  <div className="text-sm text-muted-foreground max-w-xs truncate cursor-help">
                    {agent.description || "No description"}
                  </div>
                </Tooltip>
              </TableCell>
              <TableCell className="py-4">
                <div className="space-y-1">
                  <div className="text-sm font-gc-reg text-foreground">
                    {formatDate(agent.date_created)}
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {formatTime(agent.date_created)}
                  </div>
                </div>
              </TableCell>
              <TableCell className="py-4">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground transition-colors"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreHorizontalIcon className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      className="cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloneClick(agent);
                      }}
                    >
                      <CopyIcon className="w-4 h-4 mr-2" />
                      Clone
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      className="text-destructive focus:text-destructive cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (agent.id) {
                          setDeleteDialogOpen(agent.id);
                        }
                      }}
                    >
                      <TrashIcon className="w-4 h-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>

                <AlertDialog open={deleteDialogOpen === agent.id} onOpenChange={(open) => setDeleteDialogOpen(open && agent.id ? agent.id : null)}>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle className="font-gc-med">
                        Delete Agent
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete "{agent.agent_name || "this agent"}"? This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={(e) => {
                          e.stopPropagation();
                          if (agent.id) {
                            handleDelete(agent.id);
                          }
                        }}
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <Dialog open={cloneDialogOpen === agent.id} onOpenChange={(open) => {
                  if (!open) {
                    setCloneDialogOpen(null);
                    setCloneAgentName("");
                    setCloneApplicationName("");
                  }
                }}>
                  <DialogContent onClick={(e) => e.stopPropagation()}>
                    <DialogHeader>
                      <DialogTitle className="font-gc-med">Clone Agent</DialogTitle>
                      <DialogDescription>
                        Create a copy of "{agent.agent_name || "this agent"}". A new application will be created for the cloned agent.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label htmlFor="clone-agent-name">Agent Name</Label>
                        <Input
                          id="clone-agent-name"
                          value={cloneAgentName}
                          onChange={(e) => setCloneAgentName(e.target.value)}
                          placeholder="Enter agent name"
                          autoFocus
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="clone-application-name">Application Name</Label>
                        <Input
                          id="clone-application-name"
                          value={cloneApplicationName}
                          onChange={(e) => setCloneApplicationName(e.target.value)}
                          placeholder="Enter application name"
                        />
                      </div>
                    </div>
                    <DialogFooter>
                      <Button
                        variant="ghost"
                        onClick={() => {
                          setCloneDialogOpen(null);
                          setCloneAgentName("");
                          setCloneApplicationName("");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleClone}
                        disabled={!cloneAgentName.trim() || !cloneApplicationName.trim()}
                        className="bg-maitai-green hover:bg-maitai-green/90"
                      >
                        Clone Agent
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <div className="text-sm text-muted-foreground">
            Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, totalAgents)} of {totalAgents} agents
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="gap-1"
            >
              <ChevronLeftIcon className="w-4 h-4" />
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {[...Array(totalPages)].map((_, i) => {
                const pageNum = i + 1;
                const isCurrentPage = pageNum === currentPage;
                return (
                  <Button
                    key={pageNum}
                    variant={isCurrentPage ? "default" : "ghost"}
                    size="sm"
                    onClick={() => setCurrentPage(pageNum)}
                    className={cn(
                      "w-8 h-8 p-0",
                      isCurrentPage && "bg-maitai-green text-white hover:bg-maitai-green/90"
                    )}
                  >
                    {pageNum}
                  </Button>
                );
              })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="gap-1"
            >
              Next
              <ChevronRightIcon className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
        </>
      )}
    </>
  );
};

export default AgentsTable;
export type { FilterConfig }; 